import { api } from '../services/api';
import React, { useState, useEffect, useCallback } from 'react';

const TeamGenerator = ({ token }) => {
  const [players, setPlayers] = useState([]);
  const [selectedPlayers, setSelectedPlayers] = useState([]);
  const [team1, setTeam1] = useState([]);
  const [team2, setTeam2] = useState([]);
  const [filterPosition, setFilterPosition] = useState('All');
  const [generationCount, setGenerationCount] = useState(0);

  const positions = ['All', 'GK', 'DEF', 'MID', 'FWD'];

  const loadPlayers = useCallback(async () => {
    try {
      const data = await api.getPlayers(token);
      setPlayers(data);
    } catch (error) {
      console.error('Error loading players:', error);
    }
  }, [token]);

  useEffect(() => {
    loadPlayers();
  }, [loadPlayers]);

  const togglePlayerSelection = (player) => {
    if (selectedPlayers.find(p => p.id === player.id)) {
      setSelectedPlayers(selectedPlayers.filter(p => p.id !== player.id));
    } else {
      setSelectedPlayers([...selectedPlayers, player]);
    }
  };

  const calculatePlayerScore = (player) => {
    const avgGoals = player.matches_played > 0 ? player.total_goals / player.matches_played : 0;
    const avgAssists = player.matches_played > 0 ? player.total_assists / player.matches_played : 0;
    const avgSaves = player.matches_played > 0 ? player.total_saves / player.matches_played : 0;
    return avgGoals * 3 + avgAssists * 2 + avgSaves * 1;
  };

  const shuffleArray = (array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  const generateBalancedTeams = () => {
    if (selectedPlayers.length < 2) {
      alert('Please select at least 2 operators');
      return;
    }

    const sortedPlayers = [...selectedPlayers].sort((a, b) => 
      calculatePlayerScore(b) - calculatePlayerScore(a)
    );

    const shuffled = shuffleArray(sortedPlayers);

    const t1 = [];
    const t2 = [];
    let t1Score = 0;
    let t2Score = 0;

    shuffled.forEach((player) => {
      const playerScore = calculatePlayerScore(player);
      
      const shouldGoToTeam1 = 
        (t1.length === t2.length && Math.random() > 0.5) || 
        (t1Score < t2Score) || 
        (t2.length >= Math.ceil(shuffled.length / 2)); 
      
      if (shouldGoToTeam1 && t1.length < Math.ceil(shuffled.length / 2)) {
        t1.push(player);
        t1Score += playerScore;
      } else if (t2.length < Math.floor(shuffled.length / 2)) {
        t2.push(player);
        t2Score += playerScore;
      } else {
        t1.push(player);
        t1Score += playerScore;
      }
    });

    setTeam1(shuffleArray(t1));
    setTeam2(shuffleArray(t2));
    setGenerationCount(prev => prev + 1);
  };

  const resetTeams = () => {
    setTeam1([]);
    setTeam2([]);
    setGenerationCount(0);
  };

  const getTeamTotalScore = (team) => {
    return team.reduce((sum, player) => sum + calculatePlayerScore(player), 0).toFixed(1);
  };

  const getPositionShorthand = (position) => {
    const map = {
      'Goalkeeper': 'GK',
      'Defender': 'DEF',
      'Midfielder': 'MID',
      'Forward': 'FWD'
    };
    return map[position] || position;
  };

  const filteredPlayers = players.filter(player => {
    if (filterPosition === 'All') return true;
    return getPositionShorthand(player.position) === filterPosition;
  });

  const scoreDifference = team1.length > 0 && team2.length > 0 
    ? Math.abs(parseFloat(getTeamTotalScore(team1)) - parseFloat(getTeamTotalScore(team2))).toFixed(1)
    : 0;

  return (
    <div className="space-y-6 sm:space-y-8 animate-fadeIn pb-24">
      
      {/* Header */}
      <div className="bg-surface-container border border-outline-variant/20 rounded-3xl p-6 sm:p-8 shadow-lg flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
        <div>
          <h2 className="text-on-background text-2xl font-lexend font-bold flex items-center gap-3 mb-1">
            <span className="material-symbols-outlined text-primary text-3xl">model_training</span>
            Algorithm Builder
          </h2>
          <p className="text-stone-400 font-manrope text-sm">Select personnel and generate mathematically balanced squads.</p>
          {generationCount > 0 && (
            <p className="text-primary text-[10px] font-bold uppercase tracking-widest mt-3 font-manrope flex items-center gap-1.5">
              <span className="material-symbols-outlined text-[14px]">cycle</span>
              Generation Iteration: {generationCount}
            </p>
          )}
        </div>
        
        <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
          {team1.length > 0 && (
            <button
              onClick={resetTeams}
              className="bg-error/10 hover:bg-error text-error hover:text-background px-5 py-3.5 rounded-xl font-lexend font-bold text-sm transition-colors flex items-center justify-center gap-2 active:scale-95"
            >
              <span className="material-symbols-outlined text-[20px]">restart_alt</span>
              Clear Draft
            </button>
          )}
          <button
            onClick={generateBalancedTeams}
            disabled={selectedPlayers.length < 2}
            className="bg-primary hover:bg-primary/90 text-on-primary px-6 py-3.5 rounded-xl font-lexend font-bold text-sm uppercase tracking-wider transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed shadow-[0_0_20px_rgba(249,189,34,0.2)] hover:shadow-[0_0_25px_rgba(249,189,34,0.4)] flex items-center justify-center gap-2"
          >
            <span className="material-symbols-outlined text-[20px]">shuffle</span>
            {team1.length > 0 ? 'Recalculate Split' : 'Auto-Balance'}
          </button>
        </div>
      </div>

      {/* Team Preview Cards */}
      {team1.length > 0 && team2.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 animate-fadeIn">
          
          {/* Squad Alpha (Team A) */}
          <div className="bg-surface-container-high border-2 border-primary/30 rounded-3xl p-6 sm:p-8 relative overflow-hidden shadow-lg shadow-primary/5">
            <div className="absolute -top-6 -right-6 p-4 opacity-5 pointer-events-none">
              <span className="material-symbols-outlined text-[150px] text-primary">shield</span>
            </div>
            <div className="relative z-10">
              <div className="flex items-start justify-between mb-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center shadow-lg shadow-primary/20">
                    <span className="text-on-primary font-black font-lexend text-xl">A</span>
                  </div>
                  <div>
                    <h3 className="text-on-background text-2xl font-black font-lexend">Squad Alpha</h3>
                    <p className="text-stone-400 font-manrope text-xs font-bold uppercase tracking-widest mt-1">
                      {team1.length} / {selectedPlayers.length} Operators
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-primary font-lexend font-black text-3xl">{getTeamTotalScore(team1)}</p>
                  <p className="text-stone-500 font-manrope text-[10px] font-bold uppercase tracking-widest mt-1">Power Rating</p>
                </div>
              </div>

              <div className="bg-background rounded-full h-2 overflow-hidden mb-5 border border-outline-variant/30">
                <div 
                  className="bg-primary h-full transition-all duration-1000 ease-out shadow-[0_0_10px_rgba(249,189,34,0.5)]"
                  style={{ width: `${(parseFloat(getTeamTotalScore(team1)) / (parseFloat(getTeamTotalScore(team1)) + parseFloat(getTeamTotalScore(team2)))) * 100}%` }}
                />
              </div>

              <div className="bg-background/50 border border-outline-variant/30 rounded-xl p-3 text-center">
                <p className={`text-xs font-bold font-manrope uppercase tracking-widest ${
                  scoreDifference < 2 ? 'text-emerald-400' : 
                  scoreDifference < 5 ? 'text-primary' : 
                  'text-rose-400'
                }`}>
                  {scoreDifference < 2 ? 'Optimal Balance Achieved' : 
                   scoreDifference < 5 ? 'Acceptable Variance' : 
                   'High Variance Detected'}
                </p>
                <p className="text-stone-500 text-[10px] mt-1 font-manrope">Differential: {scoreDifference}</p>
              </div>
            </div>
          </div>

          {/* Squad Bravo (Team B) */}
          <div className="bg-surface-container-high border-2 border-blue-500/30 rounded-3xl p-6 sm:p-8 relative overflow-hidden shadow-lg shadow-blue-500/5">
            <div className="absolute -top-6 -right-6 p-4 opacity-5 pointer-events-none">
              <span className="material-symbols-outlined text-[150px] text-blue-500">shield</span>
            </div>
            <div className="relative z-10">
              <div className="flex items-start justify-between mb-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/20">
                    <span className="text-white font-black font-lexend text-xl">B</span>
                  </div>
                  <div>
                    <h3 className="text-on-background text-2xl font-black font-lexend">Squad Bravo</h3>
                    <p className="text-stone-400 font-manrope text-xs font-bold uppercase tracking-widest mt-1">
                      {team2.length} / {selectedPlayers.length} Operators
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-blue-400 font-lexend font-black text-3xl">{getTeamTotalScore(team2)}</p>
                  <p className="text-stone-500 font-manrope text-[10px] font-bold uppercase tracking-widest mt-1">Power Rating</p>
                </div>
              </div>

              <div className="bg-background rounded-full h-2 overflow-hidden mb-5 border border-outline-variant/30">
                <div 
                  className="bg-blue-500 h-full transition-all duration-1000 ease-out shadow-[0_0_10px_rgba(59,130,246,0.5)]"
                  style={{ width: `${(parseFloat(getTeamTotalScore(team2)) / (parseFloat(getTeamTotalScore(team1)) + parseFloat(getTeamTotalScore(team2)))) * 100}%` }}
                />
              </div>

              <div className="bg-background/50 border border-outline-variant/30 rounded-xl p-3 text-center">
                <p className="text-stone-400 text-[10px] font-bold uppercase tracking-widest font-manrope mt-1">
                  Run Recalculation for alternate variables
                </p>
              </div>
            </div>
          </div>

        </div>
      )}

      {/* Generated Roster Lists */}
      {team1.length > 0 && team2.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 animate-fadeIn">
          {/* Alpha Players */}
          <div className="bg-surface-container border border-outline-variant/20 rounded-3xl p-6 shadow-lg">
            <h3 className="text-on-background text-lg font-lexend font-bold mb-4 flex items-center gap-2">
              <span className="w-2 h-2 bg-primary rounded-full"></span>
              Alpha Roster
            </h3>
            <div className="space-y-3">
              {team1.map(player => (
                <div key={player.id} className="bg-background border-l-4 border-l-primary border-y border-r border-outline-variant/30 rounded-xl p-3.5 flex items-center gap-4 hover:bg-surface-container-highest transition-colors">
                  <div className="w-10 h-10 bg-surface-container-highest border border-outline-variant/30 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-on-background font-lexend font-bold text-sm">{player.name.charAt(0)}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-on-background font-lexend font-bold text-sm truncate">{player.name}</p>
                    <p className="text-stone-500 font-manrope text-[10px] font-bold uppercase tracking-widest mt-0.5">{getPositionShorthand(player.position)}</p>
                  </div>
                  <div className="text-right border-l border-outline-variant/30 pl-4">
                    <p className="text-primary font-lexend font-black">{Math.round(calculatePlayerScore(player) * 10)}</p>
                    <p className="text-stone-600 font-manrope text-[9px] font-bold uppercase tracking-widest">OVR</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Bravo Players */}
          <div className="bg-surface-container border border-outline-variant/20 rounded-3xl p-6 shadow-lg">
            <h3 className="text-on-background text-lg font-lexend font-bold mb-4 flex items-center gap-2">
              <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
              Bravo Roster
            </h3>
            <div className="space-y-3">
              {team2.map(player => (
                <div key={player.id} className="bg-background border-l-4 border-l-blue-500 border-y border-r border-outline-variant/30 rounded-xl p-3.5 flex items-center gap-4 hover:bg-surface-container-highest transition-colors">
                  <div className="w-10 h-10 bg-surface-container-highest border border-outline-variant/30 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-on-background font-lexend font-bold text-sm">{player.name.charAt(0)}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-on-background font-lexend font-bold text-sm truncate">{player.name}</p>
                    <p className="text-stone-500 font-manrope text-[10px] font-bold uppercase tracking-widest mt-0.5">{getPositionShorthand(player.position)}</p>
                  </div>
                  <div className="text-right border-l border-outline-variant/30 pl-4">
                    <p className="text-blue-400 font-lexend font-black">{Math.round(calculatePlayerScore(player) * 10)}</p>
                    <p className="text-stone-600 font-manrope text-[9px] font-bold uppercase tracking-widest">OVR</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Available Pool Selection */}
      <div className="bg-surface-container border border-outline-variant/20 rounded-3xl p-6 sm:p-8 shadow-lg">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          <div>
            <h3 className="text-on-background text-xl font-lexend font-bold">Draft Pool</h3>
            <p className="text-stone-400 text-sm font-manrope mt-1">Select operators for algorithm calculation.</p>
          </div>
          <div className="bg-background border border-outline-variant/50 px-4 py-2 rounded-xl flex items-center gap-3">
            <span className="material-symbols-outlined text-primary">check_circle</span>
            <span className="text-on-background font-lexend font-bold">{selectedPlayers.length} Selected</span>
          </div>
        </div>

        {/* Filters */}
        <div className="mb-8 overflow-x-auto pb-2 custom-scrollbar flex items-center gap-2">
          {positions.map(position => (
            <button
              key={position}
              onClick={() => setFilterPosition(position)}
              className={`px-5 py-2.5 rounded-xl font-manrope font-bold text-xs whitespace-nowrap transition-all active:scale-95 ${
                filterPosition === position
                  ? 'bg-primary text-on-primary shadow-lg shadow-primary/20 border border-primary'
                  : 'bg-background text-stone-400 hover:text-on-background hover:bg-surface-container-high border border-outline-variant/30'
              }`}
            >
              {position}
            </button>
          ))}
        </div>

        {/* Players Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-4">
          {filteredPlayers.map(player => {
            const isSelected = selectedPlayers.find(p => p.id === player.id);
            const score = calculatePlayerScore(player);
            
            return (
              <button
                key={player.id}
                onClick={() => togglePlayerSelection(player)}
                className={`p-4 rounded-2xl border transition-all text-left relative overflow-hidden group ${
                  isSelected
                    ? 'border-primary bg-primary/10 shadow-[0_0_15px_rgba(249,189,34,0.15)]'
                    : 'border-outline-variant/30 hover:border-outline-variant bg-background hover:bg-surface-container-high'
                }`}
              >
                {isSelected && (
                  <div className="absolute top-0 right-0 w-8 h-8 bg-primary rounded-bl-2xl flex items-center justify-center">
                    <span className="material-symbols-outlined text-on-primary text-[16px] font-bold">check</span>
                  </div>
                )}
                
                <div className="flex flex-col gap-3">
                  <div className="w-10 h-10 bg-surface-container border border-outline-variant/30 rounded-xl flex items-center justify-center">
                    <span className="text-on-background font-lexend font-bold text-sm">
                      {player.name.charAt(0)}
                    </span>
                  </div>
                  <div>
                    <p className="text-on-background font-lexend font-bold text-sm truncate">{player.name}</p>
                    <p className="text-stone-500 font-manrope text-[10px] font-bold uppercase tracking-widest mt-0.5">{getPositionShorthand(player.position)}</p>
                  </div>
                  
                  <div className="pt-3 border-t border-outline-variant/20 flex items-center justify-between mt-1">
                    <span className="text-stone-600 font-manrope text-[9px] font-bold uppercase tracking-widest">OVR</span>
                    <span className={`font-lexend font-black text-sm ${
                      score > 5 ? 'text-emerald-400' : 
                      score > 2 ? 'text-primary' : 
                      'text-stone-400'
                    }`}>
                      {Math.round(score * 10)}
                    </span>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default TeamGenerator;
